'use client';

import type { ComponentProps } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

/**
 * Picks the right element for the destination, which is not a style question.
 *
 * A link to a section of the page you are already on has to be a plain `<a href="#id">` so
 * `SmoothHashScroll` can take it — that is the site's one scroll curve, and it also honours the
 * target's scroll-margin, moves focus and pushes history. `next/link` intercepts the click first
 * and client-navigates, which jumps.
 *
 * Anywhere else the destination is a route, and a route wants `next/link` — a plain `<a>` there is
 * a full document load with no prefetch.
 */
export function SmartLink({ href, ...rest }: { href: string } & Omit<ComponentProps<'a'>, 'href'>) {
  const pathname = usePathname();
  const [path, hash] = href.split('#');
  const samePage = Boolean(hash) && (path === '' || path === pathname || path === `${pathname}/`);

  if (samePage) return <a href={`#${hash}`} {...rest} />;
  return <Link href={href} {...rest} />;
}
